import { useState, useCallback, useRef, useEffect, RefObject } from 'react';

// Constants
import { TEXT_MEASUREMENT } from '@/constants/string';

// Utils
import {
  createLineBasedTruncatedContent,
  shouldTruncateContent,
} from '@/utils';

interface UseExpandableContentProps {
  content: string;
  maxLines?: number;
  initialDelay?: number;
  debounceDelay?: number;
  expandButtonId?: string;
  isMobile?: boolean;
}

interface UseExpandableContentReturn {
  displayContent: string;
  contentRef: RefObject<HTMLDivElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  shouldTruncate: boolean | null;
  hasExpandButton: boolean;
  isProcessing: boolean;
  isDialogOpen: boolean;
  isMobile: boolean;
  setIsDialogOpen: (open: boolean) => void;
}

/**
 * A custom hook that truncates text content by line count and provides
 * an option to expand into a dialog for full viewing.
 *
 * Features:
 * - Detects when content exceeds `maxLines` and applies truncation.
 * - Adds an "Expand" button that opens a dialog with the full content.
 * - Recalculates truncation on container resize
 * - Supports mobile-specific line limits and button positioning
 *
 * @param props Configuration: content, maxLines, delays, optional expand button id, and mobile flag.
 * @returns Processed content, dialog state handlers, refs, truncation flags, and mobile status.
 *
 * @example
 * const { displayContent, isDialogOpen, setIsDialogOpen, contentRef, containerRef, hasExpandButton } =
 *   useExpandableContent({ content, maxLines: 3, isMobile: true });
 */
export const useExpandableContent = ({
  content,
  maxLines = TEXT_MEASUREMENT.MAX_VISIBLE_LINES,
  initialDelay = 100,
  debounceDelay = 150,
  expandButtonId: customId,
  isMobile = false,
}: UseExpandableContentProps): UseExpandableContentReturn => {
  const [displayContent, setDisplayContent] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Stable expand button id, initialized once and kept throughout the lifecycle
  const expandButtonIdRef = useRef(
    customId || `expand-button-${Math.random().toString(36).slice(2, 11)}`,
  );
  const expandButtonId = expandButtonIdRef.current;

  // Track latest truncate decision for consumer flags
  const shouldTruncateRef = useRef<boolean | null>(null);

  // Track width to avoid re-processing on height-only changes
  const lastObservedWidthRef = useRef<number>(-1);

  useEffect(() => {
    shouldTruncateRef.current = null;
    lastObservedWidthRef.current = -1;
  }, [content, maxLines, isMobile]);

  // Determine the display content: truncated or full
  const determineContent = useCallback(
    (element: HTMLElement) => {
      if (!content || !element.offsetWidth) return content;

      const currentMaxLines = isMobile
        ? TEXT_MEASUREMENT.MAX_VISIBLE_LINES_MOBILE
        : maxLines;

      // Check if truncation is needed
      const needsTruncation = shouldTruncateContent(
        element,
        content,
        currentMaxLines,
      );

      shouldTruncateRef.current = needsTruncation;

      if (needsTruncation) {
        const { content: truncatedContent } = createLineBasedTruncatedContent(
          element,
          content,
          expandButtonId,
          currentMaxLines,
          isMobile,
        );
        return truncatedContent;
      }

      // Otherwise render full content
      return content;
    },
    [content, expandButtonId, maxLines, isMobile],
  );

  // Process content and update state
  const processContent = useCallback(
    (element: HTMLElement) => {
      const processed = determineContent(element);

      setDisplayContent(processed);
      setIsProcessing(false);
    },
    [determineContent],
  );

  // Initialize rendering after a short delay to avoid SSR/hydration issues
  useEffect(() => {
    setIsProcessing(true);

    const timeoutId = setTimeout(() => {
      const element = contentRef.current;

      if (element) {
        processContent(element);
      } else {
        // Fallback when ref is not ready
        setDisplayContent(content);
        setIsProcessing(false);
      }
    }, initialDelay);

    return () => clearTimeout(timeoutId);
  }, [processContent, initialDelay, content]);

  // Watch for container size changes to re-check truncation, debounced to avoid too many calls
  useEffect(() => {
    const element = contentRef.current;
    const resizeTarget = containerRef.current ?? element;

    if (!element || !resizeTarget) return;

    lastObservedWidthRef.current = element.offsetWidth || -1;

    let timeoutId: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver(() => {
      const currentWidth = element.offsetWidth || -1;

      // Truncation depends on available width, ignore height-only updates
      if (currentWidth === lastObservedWidthRef.current) return;

      lastObservedWidthRef.current = currentWidth;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => processContent(element), debounceDelay);
    });

    resizeObserver.observe(resizeTarget);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [processContent, debounceDelay]);

  // Fallback for viewport breakpoint changes where ResizeObserver may not fire
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleViewportResize = () => {
      const element = contentRef.current;
      if (!element) return;

      const currentWidth = element.offsetWidth || -1;

      if (currentWidth === lastObservedWidthRef.current) return;

      lastObservedWidthRef.current = currentWidth;

      if (currentWidth <= 0) return;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => processContent(element), debounceDelay);
    };

    window.addEventListener('resize', handleViewportResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleViewportResize);
    };
  }, [processContent, debounceDelay]);

  // Event delegation: listen for click and keydown on the expand button
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const targetSelector = '[id^="expand-button-"]';

    const handler = (event: MouseEvent | KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTarget = target.closest(targetSelector);
      const isKeyTrigger =
        event instanceof KeyboardEvent && ['Enter', ' '].includes(event.key);

      // When clicking the button or pressing Enter/Space, open the dialog
      if (isTarget && (event.type === 'click' || isKeyTrigger)) {
        event.preventDefault();
        setIsDialogOpen(true);
      }
    };

    container.addEventListener('click', handler);
    container.addEventListener('keydown', handler);

    return () => {
      container.removeEventListener('click', handler);
      container.removeEventListener('keydown', handler);
    };
  }, []);

  return {
    displayContent,
    isDialogOpen,
    setIsDialogOpen,
    contentRef,
    containerRef,
    shouldTruncate: shouldTruncateRef.current,
    hasExpandButton: Boolean(shouldTruncateRef.current),
    isProcessing,
    isMobile,
  };
};
